if (!customElements.get('floating-add-to-cart-block')) {
  customElements.define(
    'floating-add-to-cart-block',
    class FloatingAddToCart extends HTMLElement {
      #observer;
      #footerObserver;
      #abortController = new AbortController();
      #buyButtons;
      #mainPicker;
      #productForm;
      #passedBuyButtons = false;
      #hiddenAtFooter = false;

      connectedCallback() {
        this.#cacheMainElements();
        this.#bindEvents();
        this.#observeBuyButtons();
        this.#syncFromMainPicker();
      }

      disconnectedCallback() {
        this.#observer?.disconnect();
        this.#footerObserver?.disconnect();
        this.#abortController.abort();
      }

      #cacheMainElements() {
        const productId = this.dataset.productId;
        const root = this.closest('.shopify-section') || document;

        this.#mainPicker =
          root.querySelector(`variant-picker[ref="mainVariantPicker"][data-product-id="${productId}"]`) ||
          root.querySelector(`variant-picker[data-product-id="${productId}"]`);
        this.#productForm = root.querySelector(`product-form-component[data-product-id="${productId}"]`);
        this.#buyButtons = this.#productForm?.closest('.buy-buttons-block');
      }

      #bindEvents() {
        const { signal } = this.#abortController;

        this.addEventListener('change', this.#handleOptionChange, { signal });
        this.addEventListener('click', this.#handleClick, { signal });
        document.addEventListener('variant:selected', this.#handleVariantSelected, { signal });
        document.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
        document.addEventListener('quantity-selector:update', this.#handleQuantityUpdate, { signal });
        document.addEventListener('cart:update', this.#clearPending, { signal });
        document.addEventListener('cart:error', this.#clearPending, { signal });
      }

      #observeBuyButtons() {
        if (!this.#buyButtons) return;

        this.#observer = new IntersectionObserver(([entry]) => {
          if (!entry) return;
          this.#passedBuyButtons = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          this.#updateVisibility();
        });
        this.#observer.observe(this.#buyButtons);

        if (this.dataset.hideAtFooter !== 'true') return;

        const footer = document.querySelector('footer');
        if (!footer) return;

        this.#footerObserver = new IntersectionObserver(
          ([entry]) => {
            this.#hiddenAtFooter = Boolean(entry?.isIntersecting);
            this.#updateVisibility();
          },
          { rootMargin: '160px 0px 0px' }
        );
        this.#footerObserver.observe(footer);
      }

      #updateVisibility() {
        const bar = this.querySelector('.floating-add-to-cart-block__bar');
        if (bar) bar.dataset.visible = String(this.#passedBuyButtons && !this.#hiddenAtFooter);
      }

      #handleClick = (event) => {
        if (!(event.target instanceof Element)) return;

        if (event.target.closest('.floating-add-to-cart-block__toggle')) {
          this.#toggleOptions();
          return;
        }

        if (event.target.closest('.floating-add-to-cart-block__button')) {
          this.#handleAddToCart();
        }
      };

      #toggleOptions() {
        const toggle = this.querySelector('.floating-add-to-cart-block__toggle');
        const panel = this.querySelector('.floating-add-to-cart-block__expanded');
        if (!(toggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
      }

      #handleOptionChange = (event) => {
        if (!(event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement)) return;
        if (!event.target.matches('.floating-add-to-cart-block__select, .floating-add-to-cart-block__option-button input')) {
          return;
        }

        const optionValueId =
          event.target instanceof HTMLSelectElement
            ? event.target.value
            : event.target.dataset.optionValueId || event.target.value;
        const mainOption = this.#mainPicker?.querySelector(
          `[data-option-value-id="${CSS.escape(optionValueId)}"]`
        );
        if (!mainOption) return;

        this.#setPending(true);

        if (mainOption instanceof HTMLInputElement) {
          if (mainOption.checked) {
            this.#setPending(false);
            return;
          }
          mainOption.click();
          return;
        }

        if (mainOption instanceof HTMLOptionElement && mainOption.parentElement instanceof HTMLSelectElement) {
          const select = mainOption.parentElement;
          if (select.value === mainOption.value) {
            this.#setPending(false);
            return;
          }
          select.value = mainOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      #handleVariantSelected = (event) => {
        const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
        if (!picker || picker.dataset.productId !== this.dataset.productId) return;

        this.#setPending(true);
        requestAnimationFrame(() => this.#syncFromMainPicker());
      };

      #handleVariantUpdate = (event) => {
        if (event.detail?.data?.productId !== this.dataset.productId) return;

        const selector = `floating-add-to-cart-block[data-instance-id="${CSS.escape(this.dataset.instanceId)}"]`;
        const updatedSection = event.detail.data.html?.querySelector(selector);
        const updatedBar = updatedSection?.querySelector('.floating-add-to-cart-block__bar');
        const currentBar = this.querySelector('.floating-add-to-cart-block__bar');
        const wasVisible = currentBar?.dataset.visible === 'true';
        const wasExpanded =
          currentBar?.querySelector('.floating-add-to-cart-block__toggle')?.getAttribute('aria-expanded') === 'true';

        if (updatedBar && currentBar) {
          currentBar.replaceChildren(...updatedBar.cloneNode(true).childNodes);
          currentBar.dataset.visible = String(wasVisible);
          currentBar.dataset.pending = 'false';
          const toggle = currentBar.querySelector('.floating-add-to-cart-block__toggle');
          const panel = currentBar.querySelector('.floating-add-to-cart-block__expanded');
          if (toggle) toggle.setAttribute('aria-expanded', String(wasExpanded));
          if (panel instanceof HTMLElement) panel.hidden = !wasExpanded;
        } else {
          this.#syncFromMainPicker();
          this.#syncVariantFallback(event.detail.resource);
        }

        this.#cacheMainElements();
        this.#syncQuantityFromMain();
        this.#setPending(false);
      };

      #syncFromMainPicker() {
        if (!this.#mainPicker) return;

        const selectedIds = Array.from(
          this.#mainPicker.querySelectorAll('input:checked, select option:checked')
        ).map((option) => option.dataset.optionValueId);

        this.querySelectorAll('.floating-add-to-cart-block__select').forEach((select, index) => {
          const selectedId = selectedIds[index];
          if (selectedId && select instanceof HTMLSelectElement) select.value = selectedId;
        });

        this.querySelectorAll('.floating-add-to-cart-block__option').forEach((option, index) => {
          const selectedId = selectedIds[index];
          option.querySelectorAll('.floating-add-to-cart-block__option-button input').forEach((input) => {
            if (input instanceof HTMLInputElement) input.checked = input.dataset.optionValueId === selectedId;
          });
        });

        this.#syncQuantityFromMain();
      }

      #handleQuantityUpdate = (event) => {
        if (!(event.target instanceof Element)) return;

        if (event.target.closest('floating-add-to-cart-block') === this) {
          this.#syncQuantityToMain(event.detail?.quantity);
          return;
        }

        if (event.target.closest('product-form-component') === this.#productForm) {
          this.#setFloatingQuantity(event.detail?.quantity);
        }
      };

      #syncQuantityFromMain() {
        this.#setQuantity(this, this.#quantity(this.#productForm)?.getValue?.());
      }

      #syncQuantityToMain(quantity) {
        this.#setQuantity(this.#productForm, quantity);
      }

      #setFloatingQuantity(quantity) {
        this.#setQuantity(this, quantity);
      }

      #quantity(root) {
        return root?.querySelector('.floating-add-to-cart-block__quantity quantity-selector-component, quantity-selector-component');
      }

      #setQuantity(root, quantity) {
        const selector = this.#quantity(root);
        const parsedQuantity = Number(quantity);
        if (selector?.setValue && Number.isFinite(parsedQuantity)) selector.setValue(parsedQuantity);
      }

      #syncVariantFallback(variant) {
        const button = this.querySelector('.floating-add-to-cart-block__button');
        const label = this.querySelector('.floating-add-to-cart-block__button-label');
        if (!(button instanceof HTMLButtonElement) || !label) return;

        button.disabled = !variant?.available;
        if (variant?.available) {
          label.textContent = button.dataset.addText || 'Add to cart';
        } else if (variant) {
          label.textContent = button.dataset.soldOutText || 'Sold out';
        } else {
          label.textContent = button.dataset.unavailableText || 'Unavailable';
        }
      }

      #handleAddToCart = () => {
        const mainButton = this.#productForm?.querySelector('[ref="addToCartButton"]');
        if (!(mainButton instanceof HTMLButtonElement) || mainButton.disabled) return;

        const floatingQuantity = this.#quantity(this);
        if (floatingQuantity?.getValue) this.#syncQuantityToMain(floatingQuantity.getValue());

        this.#setPending(true);
        mainButton.dataset.puppet = 'true';
        mainButton.click();
      };

      #clearPending = () => {
        this.#setPending(false);
      };

      #setPending(pending) {
        const bar = this.querySelector('.floating-add-to-cart-block__bar');
        if (bar) bar.dataset.pending = String(pending);
      }
    }
  );
}
